import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { FaSeedling, FaBolt, FaClock, FaBookOpen, FaUserShield,FaHandsHelping } from "react-icons/fa";

import API_URL from "./api/API";
import { useUserAuthContext } from "./user/useUserAuthContext";

export default function Landing() {
  const AMOUNT = 1499;
  const navigate = useNavigate();
  const { dispatch } = useUserAuthContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const features = [
    {
      title: "Break free from routine",
      desc: "Stop letting mundane habits control your life. Small actions help you reclaim focus and energy for your soul.",
      icon: <FaBolt className="text-indigo-500 w-6 h-6" />,
    },
    {
      title: "Build meaningful habits",
      desc: "Actionable tasks to cultivate discipline, mindfulness, and spiritual alignment — one step at a time.",
      icon: <FaSeedling className="text-indigo-500 w-6 h-6" />,
    },
    {
      title: "Achieve inner peace",
      desc: "Align your actions with your higher purpose to experience contentment, clarity, and fulfillment.",
      icon: <FaClock className="text-indigo-500 w-6 h-6" />,
    },
    {
      title: "Inspired by Quran & Mystical Islam",
      desc: "Timeless spiritual wisdom applied practically for today’s busy, materialistic world.",
      icon: <FaBookOpen className="text-indigo-500 w-6 h-6" />,
    },
    {
      title: "Your personal journey",
      desc: "Private, personalized progress focusing on self-reform and the submission of the soul.",
      icon: <FaUserShield className="text-indigo-500 w-6 h-6" />,
    },
    {
      title: "Connect & give back",
      desc: "Encourage community, reflection, and acts of kindness to reinforce your spiritual growth.",
      icon: <FaHandsHelping className="text-indigo-500 w-6 h-6" />,
    },
  ];

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError("");
      setLoading(true);

      const credential = credentialResponse?.credential;
      if (!credential) {
        setError("Google login failed. Please try again.");
        setLoading(false);
        return;
      }

      const res = await fetch(API_URL.FRONTEND_GOOGLE_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });

      const data = await res.json();
      if (!res.ok || !data?.status) {
        setError(data?.message || "Login failed. Please try again.");
        setLoading(false);
        return;
      }
      const authPayload = {
        token: data.token,
        user: data.user,
        activeWorkoutMaster: data.activeWorkoutMaster || null,
        course: data.course || null,
      };
      
      localStorage.setItem("user", JSON.stringify(authPayload));
      
      dispatch({ type: "LOGIN", payload: authPayload });
      
      // ALWAYS go home
      navigate("/home");
    } catch (err) {
      console.error("Google login error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google login was cancelled or failed.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-gray-100 text-zinc-900 px-5 py-10 sm:px-6 sm:py-12">
      {/* Header / Name */}
      <div className="mx-auto max-w-3xl text-center mb-10">
        <div className="text-3xl font-bold text-indigo-700">SoulWorkout</div>
      </div>

      {/* Hero */}
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="text-3xl sm:text-5xl font-bold leading-tight">
          Transform your inner self, one small action at a time
        </h1>
        <p className="mt-4 text-lg sm:text-xl text-zinc-700">
          Life gets busy, distractions grow, and bad habits block your spiritual growth. 
          SoulWorkout gives you short, actionable spiritual tasks to awaken your soul, build meaningful habits, and bring true contentment — even if some days are skipped.
        </p>
      </div>

      {/* Top Signup CTA */}
      <div className="mx-auto mt-10 max-w-md">
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-lg">
          <div className="text-center text-lg font-semibold text-zinc-900">
            ₹{AMOUNT} / year
          </div>
          <div className="mt-1 text-sm text-zinc-600 text-center">
            Less than ₹5/day • Delivered via Telegram
          </div>

          <div className="mt-4 w-full">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="filled_blue"
              size="large"
              width="100%"
              text="continue_with"
              className="w-full rounded-2xl !px-6 !py-3 shadow hover:shadow-md transition"
            />

            {loading && (
              <div className="mt-3 text-sm text-zinc-600 text-center">
                Logging you in...
              </div>
            )}

            {error && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="mt-3 text-xs leading-relaxed text-zinc-500 text-center">
            After signup, we’ll show payment instructions and activate your access.
          </div>
        </div>
      </div>

      {/* Features / What you get */}
      <div className="mx-auto mt-12 max-w-3xl">
        <h2 className="text-2xl font-bold text-center text-zinc-900">
          How your soul grows
        </h2>
        <p className="mt-2 text-center text-zinc-600">
          Just as your body needs exercise, your soul needs intentional actions to thrive.
        </p>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {features.map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-4 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm hover:shadow-md transition"
            >
              <div className="mt-1">{item.icon}</div>
              <div>
                <div className="font-semibold text-zinc-900">{item.title}</div>
                <div className="mt-1 text-zinc-600 text-sm">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Signup CTA */}
      <div className="mx-auto mt-12 max-w-md text-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          theme="filled_blue"
          size="large"
          width="100%"
          text="continue_with"
          className="w-full rounded-2xl !px-6 !py-3 shadow hover:shadow-md transition"
        />
      </div>

      {/* Footer */}
      <div className="mt-12 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-500 sm:text-sm">
        © {new Date().getFullYear()} SoulWorkout • One small step at a time
      </div>
    </div>
  );
}
